import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  Tooltip,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DrawIcon from "@mui/icons-material/Draw";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { ReportActivityData } from "../types";
import { InfoTooltip } from "./InfoTooltip";

const ICON_MAP: Record<string, React.ReactNode> = {
  Viewed: <VisibilityIcon sx={{ fontSize: 16 }} />,
  Signed: <DrawIcon sx={{ fontSize: 16 }} />,
  Downloaded: <DownloadIcon sx={{ fontSize: 16 }} />,
  Updated: <EditIcon sx={{ fontSize: 16 }} />,
  Deleted: <DeleteIcon sx={{ fontSize: 16 }} />,
  Regenerated: <AutoFixHighIcon sx={{ fontSize: 16 }} />,
};

const COLOR_MAP: Record<string, "primary" | "success" | "info" | "warning" | "error"> = {
  Viewed: "primary",
  Signed: "success",
  Downloaded: "info",
  Updated: "warning",
  Deleted: "error",
  Regenerated: "info",
};

const DESCRIPTION_MAP: Record<string, string> = {
  Viewed: "Number of times reports were opened for viewing (read-only).",
  Signed: "Reports that were digitally signed by an officer.",
  Downloaded: "Reports downloaded as PDF documents.",
  Updated: "Reports that were edited and saved after initial generation.",
  Deleted: "Reports that were permanently deleted.",
  Regenerated: "AI narrative regenerations requested by users. High counts may indicate dissatisfaction with initial narratives.",
};

interface ReportActivityChartProps {
  data: ReportActivityData[];
}

export const ReportActivityChart: React.FC<ReportActivityChartProps> = ({
  data,
}) => {
  const theme = useTheme();
  const maxCount = data.length > 0 ? Math.max(...data.map((d) => d.count), 1) : 1;
  const totalActions = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Report Engagement
            </Typography>
            <InfoTooltip title="Actions taken on reports after generation. Tracks how users interact with completed reports." />
          </Box>
        }
        subheader={
          totalActions > 0
            ? `${totalActions} total actions this period`
            : "Post-creation report activity"
        }
      />
      <CardContent>
        {data.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No report activity data available
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {data.map((d) => {
              const color = COLOR_MAP[d.label] ?? "primary";
              const icon = ICON_MAP[d.label];
              return (
                <Tooltip
                  key={d.label}
                  title={`${d.count} reports ${d.label.toLowerCase()}`}
                  arrow
                  placement="left"
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ color: `${color}.main`, display: "flex" }}>
                          {icon}
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {d.label}
                        </Typography>
                        {DESCRIPTION_MAP[d.label] && (
                          <InfoTooltip title={DESCRIPTION_MAP[d.label]} />
                        )}
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                        color={`${color}.main`}
                      >
                        {d.count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(d.count / maxCount) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette[color].main, 0.1),
                        "& .MuiLinearProgress-bar": {
                          bgcolor: `${color}.main`,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
